/*
 * Hover magnifier loupe for product images.
 *
 * Replaces Dawn's click-to-activate overlay (assets/magnify.js): moving the pointer over a
 * product image shows a round magnifying glass that follows the cursor and renders that part
 * of the image at ZOOM_RATIO from a higher-resolution CDN source. No click required.
 *
 * Bound by delegation on `document`, so images swapped in after load need no re-binding —
 * variant media updates (product-info.js) and the Searay thumbnail switcher both replace the
 * <img> or its src.
 *
 * Opt-in per section via `data-zoom-on-hover` on the section root (set when the theme editor's
 * Zoom setting is "Click and hover"). Images are tagged `.image-magnify-hover` by
 * snippets/product-thumbnail.liquid and sections/main-product-searay.liquid.
 */
(function () {
  // theme-editor.js re-injects the [id^=EnableZoomOnHover] script tag on every editor change;
  // delegation is bound once, so a second run must do nothing.
  if (window.magnifyLensLoaded) return;
  window.magnifyLensLoaded = true;

  // product-info.js re-arms the legacy click magnifier after a variant media swap by calling the
  // global enableZoomOnHover() that magnify.js defines. The loupe needs no re-arming, so stub the
  // name out to keep that call from throwing when magnify.js is not on the page.
  if (typeof window.enableZoomOnHover !== 'function') {
    window.enableZoomOnHover = function () {};
  }

  var ZOOM_RATIO = 2.5;
  var LENS_SIZE_RATIO = 0.42; // of the displayed image width
  var LENS_MIN_SIZE = 150;
  var LENS_MAX_SIZE = 280;
  var ZOOM_SOURCE_WIDTH = 1946; // widest size product-thumbnail.liquid asks the CDN for

  var SELECTOR_ROOT = '[data-zoom-on-hover]';
  var SELECTOR_IMAGE = '.image-magnify-hover';
  // Below 750px Dawn lays its lightbox button over the media, so the pointer never reaches the
  // <img>; resolve the image through the media container in that case.
  var SELECTOR_MEDIA_CONTAINER = '.product-media-container, .searay-media__main';
  var CLASS_LENS = 'magnify-lens';
  var CLASS_LENS_VISIBLE = 'magnify-lens--visible';

  var POINTER_TYPE_MOUSE = 'mouse';
  var QUERY_FINE_POINTER = '(hover: hover) and (pointer: fine)';

  var OBJECT_FIT_COVER = 'cover';
  var OBJECT_FIT_CONTAIN = 'contain';
  var OBJECT_FIT_SCALE_DOWN = 'scale-down';

  var lens = null;
  var activeImage = null;
  var activeFit = OBJECT_FIT_COVER;
  var lastClientX = 0;
  var lastClientY = 0;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  // Held rather than re-queried: pointermove reads it on every move.
  var finePointerQuery = window.matchMedia(QUERY_FINE_POINTER);

  function getLens() {
    if (lens) return lens;
    lens = document.createElement('div');
    lens.className = CLASS_LENS;
    lens.setAttribute('aria-hidden', 'true');
    document.body.appendChild(lens);
    return lens;
  }

  // Same image, bigger render from the CDN — the displayed src is only sharp at 1x, so zooming it
  // alone looks soft. Left untouched when the URL is cropped or height-constrained, because
  // changing width on those distorts the framing.
  function zoomSource(image) {
    var source = image.currentSrc || image.src;
    try {
      var url = new URL(source, window.location.href);
      if (!url.searchParams.has('width')) return source;
      if (url.searchParams.has('height') || url.searchParams.has('crop')) return source;
      if (parseInt(url.searchParams.get('width'), 10) >= ZOOM_SOURCE_WIDTH) return source;
      url.searchParams.set('width', ZOOM_SOURCE_WIDTH);
      return url.toString();
    } catch (error) {
      return source;
    }
  }

  function imageFrom(target) {
    if (!(target instanceof Element) || !target.closest(SELECTOR_ROOT)) return null;
    var image = target.closest(SELECTOR_IMAGE);
    if (image) return image;
    var container = target.closest(SELECTOR_MEDIA_CONTAINER);
    return container ? container.querySelector(SELECTOR_IMAGE) : null;
  }

  function isPointerOver(image, clientX, clientY) {
    var rect = image.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }

  // The <img> box and the pixels actually painted inside it differ whenever object-fit crops or
  // letterboxes — Dawn puts `object-fit: cover` on all product media (base.css .media > img), so a
  // container that is not exactly the image's aspect ratio shows a cropped image. Map through the
  // painted rectangle so the loupe follows what the shopper actually sees. Assumes the theme's
  // `object-position: center center`.
  function paintedRect(image, rect) {
    var naturalWidth = image.naturalWidth;
    var naturalHeight = image.naturalHeight;
    var scale;

    if (!naturalWidth || !naturalHeight) return { left: 0, top: 0, width: rect.width, height: rect.height };

    if (activeFit === OBJECT_FIT_COVER) {
      scale = Math.max(rect.width / naturalWidth, rect.height / naturalHeight);
    } else if (activeFit === OBJECT_FIT_CONTAIN || activeFit === OBJECT_FIT_SCALE_DOWN) {
      scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
      if (activeFit === OBJECT_FIT_SCALE_DOWN) scale = Math.min(scale, 1);
    } else {
      // `fill` (and anything unrecognised): the image is stretched to the box.
      return { left: 0, top: 0, width: rect.width, height: rect.height };
    }

    var width = naturalWidth * scale;
    var height = naturalHeight * scale;
    return {
      left: (rect.width - width) / 2,
      top: (rect.height - height) / 2,
      width: width,
      height: height
    };
  }

  // Keeps the loupe full of visible image: it may never run past a cropped or letterboxed edge into
  // blank background. All values are along one axis, in CSS pixels of the painted image.
  function backgroundOffset(position, lensSize, paintedOffset, paintedSize, boxSize) {
    var visibleStart = Math.max(0, -paintedOffset);
    var visibleEnd = Math.min(paintedSize, boxSize - paintedOffset);
    var min = lensSize - visibleEnd * ZOOM_RATIO;
    var max = -visibleStart * ZOOM_RATIO;

    // Visible strip narrower than the loupe (very small image): centre it instead.
    if (min > max) return (min + max) / 2;
    return clamp(lensSize / 2 - position * ZOOM_RATIO, min, max);
  }

  // Holds the loupe inside the image instead of letting it hang over the header or the buy column,
  // so it reads as a glass laid on the photo. Near an edge the cursor sits off-centre in it.
  function centreWithin(value, half, start, end) {
    if (end - start <= half * 2) return (start + end) / 2;
    return clamp(value, start + half, end - half);
  }

  function render(image, clientX, clientY) {
    var rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var painted = paintedRect(image, rect);
    var element = getLens();
    var size = clamp(rect.width * LENS_SIZE_RATIO, LENS_MIN_SIZE, LENS_MAX_SIZE);
    var centreX = centreWithin(clientX, size / 2, rect.left, rect.right);
    var centreY = centreWithin(clientY, size / 2, rect.top, rect.bottom);
    var x = centreX - rect.left - painted.left;
    var y = centreY - rect.top - painted.top;

    element.style.width = size + 'px';
    element.style.height = size + 'px';
    element.style.transform = 'translate3d(' + (centreX - size / 2) + 'px, ' + (centreY - size / 2) + 'px, 0)';
    element.style.backgroundSize = painted.width * ZOOM_RATIO + 'px ' + painted.height * ZOOM_RATIO + 'px';
    element.style.backgroundPosition =
      backgroundOffset(x, size, painted.left, painted.width, rect.width) +
      'px ' +
      backgroundOffset(y, size, painted.top, painted.height, rect.height) +
      'px';
  }

  function activate(image) {
    if (activeImage === image) return;

    var displayed = image.currentSrc || image.src;
    if (!displayed) return; // srcset not resolved yet — nothing to magnify

    activeImage = image;
    // Read once per hover — getComputedStyle on every pointermove would be wasteful.
    activeFit = window.getComputedStyle(image).objectFit || OBJECT_FIT_COVER;

    var element = getLens();
    // Show the displayed resolution straight away, then sharpen once the larger render arrives —
    // no spinner, no delay on first hover.
    element.style.backgroundImage = 'url("' + displayed + '")';

    var source = zoomSource(image);
    if (source !== displayed) {
      var loader = new Image();
      loader.onload = function () {
        if (activeImage === image) element.style.backgroundImage = 'url("' + source + '")';
      };
      loader.src = source;
    }

    element.classList.add(CLASS_LENS_VISIBLE);
  }

  function deactivate() {
    activeImage = null;
    if (lens) lens.classList.remove(CLASS_LENS_VISIBLE);
  }

  // Everything runs off pointermove rather than pointerover: the gallery swaps the <img> out from
  // under a stationary cursor on every variant change, which closes the loupe, and pointerover
  // would not fire again until the shopper left the image and came back.
  document.addEventListener(
    'pointermove',
    function (event) {
      if (event.pointerType !== POINTER_TYPE_MOUSE || !finePointerQuery.matches) return;

      lastClientX = event.clientX;
      lastClientY = event.clientY;

      if (activeImage && isPointerOver(activeImage, event.clientX, event.clientY)) {
        render(activeImage, event.clientX, event.clientY);
        return;
      }

      var image = imageFrom(event.target);
      if (!image || !isPointerOver(image, event.clientX, event.clientY)) {
        if (activeImage) deactivate();
        return;
      }

      activate(image);
      if (activeImage === image) render(image, event.clientX, event.clientY);
    },
    { passive: true }
  );

  // Pointer left the document entirely (no pointermove follows to close the loupe).
  document.addEventListener('pointerout', function (event) {
    if (activeImage && !event.relatedTarget) deactivate();
  });

  // The image moves under a stationary cursor while scrolling, so the mapping goes stale.
  window.addEventListener(
    'scroll',
    function () {
      if (!activeImage) return;
      if (!isPointerOver(activeImage, lastClientX, lastClientY)) {
        deactivate();
        return;
      }
      render(activeImage, lastClientX, lastClientY);
    },
    { passive: true, capture: true }
  );

  window.addEventListener('resize', deactivate, { passive: true });
})();
